import React from "react";
import { Linking } from "react-native";
import styled from "styled-components/native";
import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import LabelView from "@components/molecules/LabelView";
import CollapsibleView from "@components/molecules/CollapsibleView";
import LineViewContainer from "./LineView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import * as Clipboard from "expo-clipboard";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";

type QuoteSenderDetailsCardProps = {
  titleKey?: string;
  titleFallback?: string;
  label?: string;
  companyName?: string;
  companyLocation?: string;
  contactName?: string;
  email?: string;
  officePhone?: string;
  officePhoneCountryCode?: string;
  mobilePhone?: string;
  mobilePhoneCountryCode?: string;
  shareLink?: string;
};

const QuoteSenderDetailsCard = ({
  titleKey = "requestHistoryDetail.receiverdetails.text",
  titleFallback = "Receiver's Details",
  label,
  companyName,
  companyLocation,
  contactName,
  email,
  officePhone,
  officePhoneCountryCode,
  mobilePhone,
  mobilePhoneCountryCode,
  shareLink,
}: QuoteSenderDetailsCardProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const keyWidthPercent = theme.isMobile ? 35 : 18;
  const keyTypographyStyle = theme.isLargeScreen
    ? typography["Body-R"]
    : typography["Minor-R"];
  const valueTypographyStyle = theme.isLargeScreen
    ? typography["Body-M"]
    : typography["Minor-M"];

  const shareableLinkView = () => (
    <ShareableLinkRow>
      <ShareableLinkContainer>
        <PressableContainer
          onPress={async () => {
            await Clipboard.setStringAsync(shareLink ?? "");
            showToast(
              t("requestHistoryDetail.success.text", "Success"),
              t("requestHistoryDetail.copyURL.toastMessage.text"),
              ToastTypes.SUCCESS,
              true,
              true
            );
          }}
        >
          <VDBIcon
            color={theme["main-01"]}
            size={16}
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.COPY}
          />
          <VDBText type={typography["Minor-M"]} color={theme["link-static"]}>
            {t("requestHistoryDetail.copyURL.text", "Copy URL")}
          </VDBText>
        </PressableContainer>
        <HorizontalSeparator />
      </ShareableLinkContainer>
      <PressableContainer
        onPress={async () => {
          if (!shareLink) return;
          const supported = await Linking.canOpenURL(shareLink);
          if (supported) {
            Linking.openURL(shareLink);
          }
        }}
      >
        <VDBIcon
          color={theme["main-01"]}
          size={16}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.EXTERNAL_LINK}
        />
        <VDBText type={typography["Minor-M"]} color={theme["link-static"]}>
          {t("requestHistoryDetail.openInNewTab.text", "Open in new tab")}
        </VDBText>
      </PressableContainer>
    </ShareableLinkRow>
  );

  return (
    <CardContainer>
      <CollapsibleView
        initExpanded={true}
        applyTopPadding={false}
        applyShadow
        arrowStyling={{ color: theme["core-04"] }}
        title={
          <StyledView>
            <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
              {label ||
                t(titleKey, {
                  defaultValue: titleFallback,
                })}
            </VDBText>
            {!!companyName && (
              <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                {companyName}
              </VDBText>
            )}
            {!!companyLocation && (
              <VDBText
                type={
                  theme.isLargeScreen
                    ? typography["Body-R"]
                    : typography["Minor-R"]
                }
                color={theme["core-06"]}
              >
                {companyLocation}
              </VDBText>
            )}
          </StyledView>
        }
      >
        <LineViewContainer background={"core-03"} />
        <CompanyDetailBottomContainer>
          <LabelViewContainer>
            <LabelView
              itemLeftText={t("requestHistoryDetail.contact.text", "Contact")}
              itemRightText={contactName || "-"}
              keyTypographyStyle={keyTypographyStyle}
              valueTypographyStyle={valueTypographyStyle}
              leftPostfixText=""
              isFixedKeyWidth={true}
              keyWidthPercent={keyWidthPercent}
              ellipsize={false}
            />
          </LabelViewContainer>
          {!!email && (
            <LabelViewContainer>
              <LabelView
                itemLeftText={t("requestHistoryDetail.email.text", "Email")}
                itemRightText={email}
                keyTypographyStyle={keyTypographyStyle}
                valueTypographyStyle={valueTypographyStyle}
                leftPostfixText=""
                isFixedKeyWidth={true}
                keyWidth={50}
                keyWidthPercent={keyWidthPercent}
                ellipsize={false}
                isFixWidth
                isFromMatchPair
                emailClickable={true}
              />
            </LabelViewContainer>
          )}
          {!!officePhone && (
            <LabelViewContainer>
              <LabelView
                itemLeftText={t("requestHistoryDetail.office.text", "Office")}
                itemRightText={officePhone}
                countryCode={officePhoneCountryCode}
                keyTypographyStyle={keyTypographyStyle}
                valueTypographyStyle={valueTypographyStyle}
                leftPostfixText=""
                isFixedKeyWidth={true}
                keyWidthPercent={keyWidthPercent}
                ellipsize={false}
                clickableHandle={true}
              />
            </LabelViewContainer>
          )}
          {!!mobilePhone && (
            <LabelViewContainer>
              <LabelView
                itemLeftText={t("requestHistoryDetail.mobile.text", "Mobile")}
                itemRightText={mobilePhone}
                countryCode={mobilePhoneCountryCode}
                keyTypographyStyle={keyTypographyStyle}
                valueTypographyStyle={valueTypographyStyle}
                leftPostfixText=""
                isFixedKeyWidth={true}
                keyWidthPercent={keyWidthPercent}
                ellipsize={false}
                clickableHandle={true}
              />
            </LabelViewContainer>
          )}
          {!!shareLink && (
            <LabelViewContainer>
              <LabelView
                itemLeftText={t(
                  "requestHistoryDetail.shareableLink.text",
                  "Sharable Link"
                )}
                itemRightView={shareableLinkView()}
                keyTypographyStyle={keyTypographyStyle}
                valueTypographyStyle={valueTypographyStyle}
                leftPostfixText=""
                isFixedKeyWidth={true}
                keyWidthPercent={keyWidthPercent}
                ellipsize={false}
              />
            </LabelViewContainer>
          )}
        </CompanyDetailBottomContainer>
      </CollapsibleView>
    </CardContainer>
  );
};

const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
`;

const CompanyDetailBottomContainer = styled.View`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
`;

const LabelViewContainer = styled.View`
  padding: ${SpaceType.ExtraSmall}px ${SpaceType.Double}px;
  overflow: hidden;
`;

const ShareableLinkContainer = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  align-items: center;
`;

const ShareableLinkRow = styled.View`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row")};
  gap: ${SpaceType.Basic}px;
`;

const PressableContainer = styled.TouchableOpacity`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  align-items: center;
`;

const HorizontalSeparator = styled.View`
  height: 16px;
  width: 1px;
  background-color: ${({ theme }) => theme["core-05"]};
`;

const StyledView = styled.View`
  gap: ${SpaceType.ExtraSmall}px;
`;

export default QuoteSenderDetailsCard;
